/** Pure geometry shared by the canvas and vector export. No persisted routing state. */
export interface RoutePoint { x: number; y: number }
export interface RouteBox extends RoutePoint { id: string; width: number; height: number }
export interface RouteEdge { id: string; source: string; target: string }
export interface RouteCommand { op: 'M' | 'L' | 'C'; values: number[] }
export interface FamilyRoute {
  shared: RouteCommand[][]
  branch: RouteCommand[]
  label: RoutePoint
}
export const ROUTE_COLOR = '#858585'
const CLEARANCE = 12
const RADIUS = 8

export function roundedRoute(points: RoutePoint[]): RouteCommand[] {
  const clean = points.filter((p, i) => !i || p.x !== points[i - 1].x || p.y !== points[i - 1].y)
  if (!clean.length) return []
  const commands: RouteCommand[] = [{ op: 'M', values: [clean[0].x, clean[0].y] }]
  for (let i = 1; i < clean.length - 1; i++) {
    const a = clean[i - 1], b = clean[i], c = clean[i + 1]
    const before = Math.hypot(b.x - a.x, b.y - a.y)
    const after = Math.hypot(c.x - b.x, c.y - b.y)
    const r = Math.min(RADIUS, before / 2, after / 2)
    const p = { x: b.x + (a.x - b.x) * r / before, y: b.y + (a.y - b.y) * r / before }
    const q = { x: b.x + (c.x - b.x) * r / after, y: b.y + (c.y - b.y) * r / after }
    commands.push({ op: 'L', values: [p.x, p.y] }, {
      op: 'C', values: [p.x + (b.x - p.x) * 2 / 3, p.y + (b.y - p.y) * 2 / 3,
        q.x + (b.x - q.x) * 2 / 3, q.y + (b.y - q.y) * 2 / 3, q.x, q.y],
    })
  }
  const end = clean[clean.length - 1]
  commands.push({ op: 'L', values: [end.x, end.y] })
  return commands
}

export const routePath = (commands: RouteCommand[]) =>
  commands.map(c => `${c.op} ${c.values.join(',')}`).join(' ')

function blocked(a: RoutePoint, b: RoutePoint, boxes: RouteBox[]): boolean {
  return boxes.some(o => a.x === b.x
    ? a.x > o.x && a.x < o.x + o.width && Math.max(a.y, b.y) > o.y && Math.min(a.y, b.y) < o.y + o.height
    : a.y > o.y && a.y < o.y + o.height && Math.max(a.x, b.x) > o.x && Math.min(a.x, b.x) < o.x + o.width)
}

/** Visibility grid only used for obstructed/manual routes. Stable coordinate
 * ordering breaks ties; impossible enclosed endpoints retain a deterministic
 * outside lane instead of moving boxes or dropping a relationship. */
function detour(start: RoutePoint, end: RoutePoint, boxes: RouteBox[], lane: number): RoutePoint[] {
  const simple = [start, { x: start.x, y: end.y }, end]
  if (!blocked(simple[0], simple[1], boxes) && !blocked(simple[1], end, boxes)) return simple
  const xs = [...new Set([start.x, end.x, ...boxes.flatMap(b => [b.x - CLEARANCE, b.x + b.width + CLEARANCE])])].sort((a, b) => a - b)
  const ys = [...new Set([start.y, end.y, ...boxes.flatMap(b => [b.y - CLEARANCE, b.y + b.height + CLEARANCE])])].sort((a, b) => a - b)
  const points = ys.flatMap(y => xs.map(x => ({ x, y })))
  const index = (p: RoutePoint) => ys.indexOf(p.y) * xs.length + xs.indexOf(p.x)
  const first = index(start), last = index(end)
  const distances = new Map<number, number>([[first, 0]])
  const previous = new Map<number, number>()
  const open = new Set([first])
  while (open.size) {
    let current = -1, best = Infinity
    for (const id of open) if (distances.get(id)! < best) { current = id; best = distances.get(id)! }
    if (current === last) {
      const path = [end]
      while (previous.has(current)) { current = previous.get(current)!; path.unshift(points[current]) }
      // Remove collinear vertices before rounding.
      return path.filter((p, i) => !i || i === path.length - 1 ||
        !((path[i - 1].x === p.x && p.x === path[i + 1].x) || (path[i - 1].y === p.y && p.y === path[i + 1].y)))
    }
    open.delete(current)
    const x = current % xs.length, y = Math.floor(current / xs.length)
    const neighbors = [x > 0 ? current - 1 : -1, x < xs.length - 1 ? current + 1 : -1,
      y > 0 ? current - xs.length : -1, y < ys.length - 1 ? current + xs.length : -1]
    for (const next of neighbors) {
      if (next < 0 || blocked(points[current], points[next], boxes)) continue
      const distance = best + Math.abs(points[current].x - points[next].x) + Math.abs(points[current].y - points[next].y) + 0.01
      if (distance >= (distances.get(next) ?? Infinity)) continue
      distances.set(next, distance); previous.set(next, current); open.add(next)
    }
  }
  const outside = Math.max(start.x, end.x, ...boxes.map(b => b.x + b.width)) + CLEARANCE + lane * 8
  return [start, { x: outside, y: start.y }, { x: outside, y: end.y }, end]
}

export function familyRoutes(boxes: RouteBox[], edges: RouteEdge[]): Map<string, FamilyRoute> {
  const byId = new Map(boxes.map(b => [b.id, b]))
  // Expanded obstacles leave room for rounding and the visible stroke.
  const obstacles = boxes.map(b => ({ ...b, x: b.x - RADIUS, y: b.y - RADIUS,
    width: b.width + 2 * RADIUS, height: b.height + 2 * RADIUS }))
  const families = new Map<string, RouteEdge[]>()
  for (const edge of [...edges].sort((a, b) => a.id.localeCompare(b.id))) {
    if (!byId.has(edge.source) || !byId.has(edge.target)) continue
    families.set(edge.source, [...(families.get(edge.source) ?? []), edge])
  }
  const result = new Map<string, FamilyRoute>()
  const usedBuses: { y: number; left: number; right: number }[] = []
  for (const [parentId, children] of families) {
    const parent = byId.get(parentId)!
    const source = { x: parent.x + parent.width / 2, y: parent.y + parent.height }
    const targets = children.map(e => {
      const b = byId.get(e.target)!
      return { x: b.x + b.width / 2, y: b.y }
    })
    const top = Math.min(...targets.map(t => t.y))
    let busY = (source.y + top) / 2
    const left = Math.min(source.x, ...targets.map(t => t.x))
    const right = Math.max(source.x, ...targets.map(t => t.x))
    for (const bus of usedBuses) {
      if (Math.abs(bus.y - busY) < 8 && left <= bus.right && right >= bus.left &&
          busY + 8 < top - CLEARANCE) busY += 8
    }
    const unrelated = obstacles.filter(b => b.id !== parentId && !children.some(e => e.target === b.id))
    const ordinary = top >= source.y + CLEARANCE * 2 &&
      !blocked(source, { x: source.x, y: busY }, unrelated) &&
      !blocked({ x: left, y: busY }, { x: right, y: busY }, unrelated) &&
      targets.every((t, i) => !blocked({ x: t.x, y: busY }, t, obstacles.filter(b => b.id !== children[i].target && b.id !== parentId)))
    const shared: RouteCommand[][] = []
    if (ordinary && children.length > 1) {
      shared.push(roundedRoute([source, { x: source.x, y: busY }]))
      const min = Math.min(...targets.map(t => t.x)), max = Math.max(...targets.map(t => t.x))
      // Outer bends belong to their branches, never duplicated by the bus.
      const l = Math.min(source.x, min + RADIUS), r = Math.max(source.x, max - RADIUS)
      shared.push(roundedRoute([{ x: l, y: busY }, { x: r, y: busY }]))
      usedBuses.push({ y: busY, left, right })
    } else if (!ordinary && children.length > 1) {
      shared.push(roundedRoute([source, { x: source.x, y: source.y + CLEARANCE }]))
    }
    children.forEach((edge, i) => {
      const target = targets[i]
      let points: RoutePoint[]
      if (ordinary) {
        if (children.length === 1) {
          points = source.x === target.x ? [source, target] :
            [source, { x: source.x, y: busY }, { x: target.x, y: busY }, target]
        } else {
          const min = Math.min(...targets.map(t => t.x)), max = Math.max(...targets.map(t => t.x))
          const offset = target.x === min && target.x < source.x ? RADIUS :
            target.x === max && target.x > source.x ? -RADIUS : 0
          points = [{ x: target.x + offset, y: busY }, { x: target.x, y: busY }, target]
        }
      } else {
        const start = { x: source.x, y: source.y + CLEARANCE }
        const end = { x: target.x, y: target.y - CLEARANCE }
        points = [...(children.length === 1 ? [source] : []),
          ...detour(start, end, obstacles, i), target]
      }
      result.set(edge.id, {
        shared: i === 0 ? shared : [],
        branch: roundedRoute(points),
        label: { x: target.x, y: target.y - 24 },
      })
    })
  }
  return result
}
